<template>
  <div class="admin_wrapper">
    <div class="left" v-if="!filterBody">
      <div class="title">{{ $t("super_user.title") }}</div>
      <div class="left-header">
        <v-pagination
          v-model="page"
          :pages="paginated"
          :range-size="1"
          @update:modelValue="setPage"
        />
        <div class="empty">
          <input v-model="page" type="number" min="1" :max="paginated" />
          <h2 @click="getPage(page)">{{ $t("super_user.set_page") }}</h2>
        </div>
        <div class="count_news">
          <span>{{ adminCount }}</span> {{ $t("super_user.news_count") }}
        </div>
        <div class="search">
          <input type="text" v-model="searchQuery" />
          <button @click="searchBar">{{ $t("super_user.search") }}</button>
        </div>
        <button v-if="isIpad" class="filter" @click="showFilter()">
          {{ $t("super_user.filter") }}
        </button>
      </div>
      <table class="left-body-container">
        <thead class="left-body-head">
          <tr class="id" @click="handleSort('id')">
            <span> {{ $t("super_user.id") }} </span>
            <div class="icon" v-if="sortedBy === 'id' || sortedBy === '-id'">
              <i class="fas" :class="`fa-caret-${show.id ? 'up' : 'down'}`"></i>
            </div>
          </tr>
          <tr class="head-title" @click="handleSort('title')">
            <span> {{ $t("super_user.news_title") }} </span>
            <div
              class="icon"
              v-if="sortedBy === 'title' || sortedBy === '-title'"
            >
              <i
                class="fas"
                :class="`fa-caret-${show.title ? 'up' : 'down'}`"
              ></i>
            </div>
          </tr>
          <tr class="author">
            {{
              $t("super_user.news_author")
            }}
          </tr>
          <tr class="category">
            {{
              $t("super_user.news_category")
            }}
          </tr>
          <tr class="date" @click="handleSort('pub_date')">
            <span> {{ $t("super_user.news_date") }}</span>
            <div
              class="icon"
              v-if="sortedBy === 'pub_date' || sortedBy === '-pub_date'"
            >
              <i
                class="fas"
                :class="`fa-caret-${show.pub_date ? 'up' : 'down'}`"
              ></i>
            </div>
          </tr>
          <tr class="status">
            {{
              $t("super_user.news_status")
            }}
          </tr>
          <tr class="draft" @click="handleSort('draft')">
            <span> {{ $t("super_user.draft") }}</span>
            <div
              class="icon"
              v-if="sortedBy === 'draft' || sortedBy === '-draft'"
            >
              <i
                class="fas"
                :class="`fa-caret-${show.draft ? 'up' : 'down'}`"
              ></i>
            </div>
          </tr>
        </thead>
        <tbody
          v-for="(item, index) in adminNews"
          :key="index"
          :class="{ styleChanged: index % 2 == 0 }"
        >
          <tr class="user-id">
            {{
              item.id
            }}
          </tr>
          <router-link
            :to="
              $i18nRoute({
                name: 'EditArticle_StaffDetail',
                params: {
                  id: item.id,
                  lang: `edit-${i18n.global.locale.value}`,
              }})
            "
            class="user-title"
          >
            {{ item.title }}</router-link
          >
          <tr class="author">
            {{
              item.author.username
            }}
          </tr>
          <tr class="news-category">
            {{
              item.sub_category.name
            }}
          </tr>
          <tr class="published-date">
            {{
              handleDate(item.pub_date)
            }},
            {{
              handleTime(item.pub_date)
            }}
          </tr>
          <tr class="selected-status">
            {{
              item?.status
            }}
          </tr>
          <tr class="draft">
            <input type="checkbox" v-model="item.draft" />
          </tr>
        </tbody>
      </table>
      <div v-if="isIpad" class="isIpad">
        <h2>
          {{ $t("super_user.move") }} {{ pageSize }}
          {{ $t("super_user.from") }} <span> {{ adminCount }} </span>
          {{ $t("super_user.news_count") }}
        </h2>
        <v-pagination
          v-model="page"
          :pages="paginated"
          :range-size="1"
          @update:modelValue="setPage"
        />
      </div>
    </div>

    <div class="right" v-if="!isIpad || filterBody">
      <div class="right-title">
        <h2>{{ $t("super_user.filter") }}</h2>
        <i v-if="isIpad" class="fas fa-times" @click="hideFilter()"></i>
      </div>
      <div class="clear" v-if="show.clear" @click="clearFilter()">
        <i class="fas fa-times"></i>
        <span> {{ $t("super_user.clear") }} </span>
      </div>
      <form>
        <div class="form-title">{{ $t("super_user.news_date") }}</div>
        <div class="calendar">
          <div class="date">
            <Datepicker
              :placeholder="$t('super_user.from_date')"
              v-model="date.pub__gte"
            >
            </Datepicker>
          </div>
          <div class="date">
            <Datepicker
              v-model="date.pub__lte"
              :placeholder="$t('super_user.to_date')"
            >
            </Datepicker>
          </div>
        </div>
        <div class="btn">
          <button @click.prevent="searchByDate()">
            {{ $t("super_user.search") }}
          </button>
          <button @click.prevent="clearDate()">
            {{ $t("super_user.reset") }}
          </button>
        </div>
      </form>

      <div class="divider"></div>

      <div
        class="user-list-container"
        :class="{ users_animation: show.user_list }"
      >
        <div class="user-list-title">
          <span> {{ $t("super_user.news_author") }} </span>
          <div class="icon" @click="show.user_list = !show.user_list">
            <i class="fas fa-chevron-down" v-if="!show.user_list"></i>
            <i class="fas fa-chevron-up" v-if="show.user_list"></i>
          </div>
        </div>
        <div class="user-list" v-if="show.user_list">
          <button @click="clearGettedName('author')">
            {{ $t("super_user.all") }}
          </button>
          <button
            @click="filterByName({ author: item.id })"
            v-for="(item, index) in author_list"
            :key="index"
          >
            {{ item.username }}
          </button>
        </div>
      </div>

      <div class="category-list-container">
        <div class="category-list-title">
          <span> {{ $t("super_user.news_category") }} </span>
          <div class="icon" @click="show.category_list = !show.category_list">
            <i class="fas fa-chevron-down" v-if="!show.category_list"></i>
            <i class="fas fa-chevron-up" v-show="show.category_list"></i>
          </div>
        </div>
        <div class="category-list" v-if="show.category_list">
          <button @click="clearGettedName('sub_category')">all</button>
          <button
            v-for="(item, index) in category_list"
            :key="index"
            @click="filterByName({ sub_category: item.id })"
          >
            {{ item.name }}
          </button>
        </div>
      </div>

      <div class="drafts">
        <div class="drafts-title">{{ $t("super_user.draft") }}</div>
        <div class="drafts-btn">
          <button @click="clearGettedName('draft')">Все</button>
          <button @click="filterByName({ draft: 'true' })">
            {{ $t("super_user.yes") }}
          </button>
          <button @click="filterByName({ draft: 'false' })">
            {{ $t("super_user.no") }}
          </button>
        </div>
      </div>

      <div class="status-list">
        <h2>{{ $t("super_user.news_status") }}</h2>
        <button @click="clearGettedName('status')">
          {{ $t("super_user.all") }}
        </button>
        <button @click="filterByName({ status: 'published' })">
          published
        </button>
        <button @click="filterByName({ status: 'moderation' })">
          moderation
        </button>
        <button @click="filterByName({ status: 'declined' })">declined</button>
      </div>
    </div>
  </div>
</template>

<script>
import { reactive, computed, watch, ref, onBeforeUnmount } from "vue";
import { useLanguage } from "@/utils/language";
import { useStore } from "vuex";
import { useFormatter } from "@/utils/formatter";
import VPagination from "@hennge/vue3-pagination";
import "@hennge/vue3-pagination/dist/vue3-pagination.css";
import Datepicker from 'vue3-date-time-picker';
import 'vue3-date-time-picker/dist/main.css';

export default {
  components: {
    VPagination,
    Datepicker,
  },
  setup() {
    const store = useStore();
    const { i18n } = useLanguage();
    const pageSize = 100;

    const filterBody = ref();
    const isIpad = ref();

    const page = ref(1);

    // sortBy
    const sortedBy = ref();

    // date
    const date = reactive({
      pub__lte: "",
      pub__gte: "",
    });

    // filter
    const filtersQuery = ref({});
    const searchQuery = ref("");
    const searchId = ref();

    // pagination
    const paginated = ref();

    const searchBar = () => {
      refreshData();
    };

    const show = reactive({
      user_list: false,
      category_list: false,
      id: false,
      title: false,
      pub_date: false,
      draft: false,
      clear: false,
    });

    if (typeof window !== "undefined") {
      isIpad.value = window.innerWidth <= 1366;
      window.addEventListener("resize", handleResize);
    }

    const handleResize = () => {
      isIpad.value = window.innerWidth <= 1366;
    };

    onBeforeUnmount(() => {
      if (typeof window !== "undefined") {
        window.removeEventListener("resize", handleResize);
      }
    });

    const showFilter = () => {
      filterBody.value = true;
    };

    if (!isIpad.value) {
      filterBody.value = false;
    }

    const hideFilter = () => {
      filterBody.value = false;
    };

    const getPage = (page) => {
      // activePage[page]
      setPage(page);
    };

    const showUserList = () => {
      show.user_list.value = true;
    };

    const loadNews = () => {
      store.dispatch("getAdminNews", {
        pageSize,
        cb: () => {
          pagination();
        },
        ordering: sortedBy.value,
        search: searchQuery.value,
        filters: filtersQuery.value,
      });
    };

    const setPage = (page) => {
      store.commit("setAdminPage", page - 1);
      loadNews();
    };

    const refreshData = () => {
      store.commit("flushAdminNews");
      setPage(1);
    };

    const pagination = () => {
      paginated.value = Math.ceil(store.getters.adminCount / pageSize);
    };

    const goToNews = () => {};

    const handleSort = (prop) => {
      sortedBy.value = sortedBy.value === prop ? `-${prop}` : prop;
      refreshData();
    };

    const filterByName = (filterItem) => {
      filtersQuery.value = {
        ...filtersQuery.value,
        ...filterItem,
      };
      show.clear = true;
      refreshData();
      filterBody.value = false;
    };

    const clearFilter = () => {
      filtersQuery.value = {};
      show.clear = false;
      filterBody.value = false;
      refreshData();
    };

    const clearGettedName = (fieldName) => {
      filtersQuery.value[fieldName] = "";
      refreshData();
    };

    const searchByDate = () => {
      filterByName({
        pub_date__gte: date.pub__gte.toISOString(),
        pub_date__lte: date.pub__lte.toISOString(),
      });

      filterBody.value = false;
    };

    const clearDate = () => {
      (date.pub__lte = ""),
        (date.pub__gte = ""),
        filterByName({
          pub_date__gte: "",
          pub_date__lte: "",
        });

      filterBody.value = false;
    };

    refreshData();
    store.dispatch("getAuthorsList", {});
    store.dispatch("getSubCategories", {
      preferredLang: i18n.global.locale.value,
    });

    watch(() => i18n.global.locale.value, refreshData);
    return {
      show,
      showUserList,
      i18n,
      ...useFormatter(),
      refreshData,
      setPage,
      pageSize,
      adminNews: computed(() => store.getters.adminNews),
      adminCount: computed(() => store.getters.adminCount),

      // pagination
      pagination,
      paginated,
      // activePage,

      // searchBar
      searchBar,
      searchQuery,
      searchId,
      goToNews,

      //list
      author_list: computed(() => store.getters.authorsList),
      category_list: computed(() => store.getters.subCategories),

      // sortedBy
      sortedBy,
      handleSort,
      filterBody,
      showFilter,
      hideFilter,
      isIpad,
      getPage,

      // filter
      filterByName,
      clearFilter,
      clearGettedName,

      // date
      date,
      searchByDate,
      clearDate,
      page,
    };
  },
};
</script>

<style>
.PaginationControl {
  display: none !important;
}
.Page {
  background: var(--btn-primary) !important;
  color: var(--light);
  width: 1.354vw;
  height: 1.354vw;
  font-size: 0.729vw;
  line-height: 0.885vw;
  border-radius: 0px !important;
}
.Page-active {
  background: transparent !important;
  color: var(--font-black);
  border: transparent;
}

.dp__clear_icon {
  display: none !important;
}
.ck-rounded-corners {
  background: var(--light) !important;
}

.dp__input {
  font-size: 0.885vw !important;
  width: 10vw !important;
}
.dp__icon {
  width: 0.967vw !important;
  height: 0.967vw !important;
}

.dp__input_icon_pad {
  padding-left: 2vw !important;
}

@media screen and (max-width: 1366px) {
  .Page {
    width: 3.385vw;
    height: 3.385vw;
    font-size: 1.8vw;
    line-height: 2.2vw;
  }
  .dp__input {
    font-size: 1.885vw !important;
    width: 30.771vw !important;
  }
  .dp__icon {
    width: 1.967vw !important;
    height: 1.967vw !important;
  }

  .dp__input_icon_pad {
    padding-left: 5vw !important;
  }
}

@media screen and (max-width: 414px) {
  .Page {
    width: 6.933vw;
    height: 6.933vw;
    font-size: 3.7vw;
    line-height: 4.5vw;
  }
  .dp__input {
    font-size: 2.885vw !important;
    width: 40.771vw !important;
  }
  .dp__icon {
    width: 2.967vw !important;
    height: 2.967vw !important;
  }

  .dp__input_icon_pad {
    padding-left: 8vw !important;
  }
}
</style>

<style lang="scss" scoped>
.admin_wrapper {
  --font-black: rgba(18, 18, 18, 1);
  --font-black-opacity: rgba(69, 69, 69, 1);
  --red: rgba(192, 21, 22, 1);
  --font-date: rgba(74, 74, 74, 1);
  --list-opacity-seven: rgba(69, 69, 69, 0.7);

  --user-id: rgba(88, 101, 116, 1);
  --user-title: rgba(20, 63, 123, 1);
  --icon-color: rgba(44, 84, 141, 1);
  --user-category: rgba(74, 74, 74, 1);

  --index2: rgba(226, 226, 226, 1);

  --btn-primary: rgba(108, 143, 186, 1);

  --divider: rgba(196, 196, 196, 1);

  --light: rgba(255, 255, 255, 1);

  display: flex;
  justify-content: space-between;
  gap: 1vw;

  .left {
    display: flex;
    flex-direction: column;
    width: 58vw;
    .title {
      font-size: 1.146vw;
      line-height: 1.354vw;
      font-weight: 300;
      color: var(--font-black);
      margin-top: 0.781vw;
    }
    .left-header {
      display: grid;
      grid-template-columns: 12vw 21vw 10vw 15vw;
      align-items: center;
      margin-top: 0.99vw;

      .pagination {
        display: flex;
        gap: 0.313vw;
        li {
          list-style: none;
          button {
            width: 1.354vw;
            height: 1.354vw;
            background: var(--btn-primary);
            color: var(--light);
            cursor: pointer;
          }
        }
      }

      .empty {
        display: flex;
        align-items: center;
        input {
          margin-right: 0.521vw;
          width: 2.823vw;
          height: 1.354vw;
          border: 1px solid var(--btn-primary);
          background: var(--light);
          outline: none;
          text-align: center;
          font-size: 0.9vw;
        }
        h2 {
          font-size: 0.729vw;
          line-height: 0.885vw;
          font-weight: 400;
          color: var(--btn-primary);
          cursor: pointer;
        }
      }

      .count_news {
        color: var(--font-black-opacity);
        span {
          font-weight: 500;
        }

        font-size: 0.729vw;
        line-height: 0.885vw;
        font-weight: 400;
      }

      .search {
        display: flex;
        input {
          width: 12.24vw;
          height: 1.615vw;
          border: 1px solid var(--btn-primary);
          outline: none;
          padding-left: 0.5vw;
          border-radius: none;
        }

        button {
          font-size: 0.8vw;
          width: 4.01vw;
          height: 1.615vw;
          background: var(--btn-primary);
          color: var(--light);
          outline: none;
          cursor: pointer;
        }
      }
    }

    table {
      display: flex;
      flex-direction: column;

      thead {
        display: grid;
        grid-template-columns: 3vw 13vw 10vw 10vw 8vw 8vw 5vw;
        font-size: 0.625vw;
        line-height: 0.781vw;
        font-weight: 500;
        color: var(--light);
        background: var(--btn-primary);
        padding: 0 1vw;
        position: relative;
        height: 1.354vw;
        margin-top: 0.521vw;
        .icon {
          margin-left: 0.2vw;
          i {
            cursor: pointer;
          }
        }
        .id,
        .head-title,
        .author,
        .category,
        .date,
        .status,
        .draft {
          display: flex;
          align-items: center;
        }

        .id,
        .head-title,
        .date,
        .draft {
          cursor: pointer;
        }

        .draft {
          display: flex;
          align-items: center;
          justify-content: center;
          margin-left: 0.417vw;
          input {
            width: 0.729vw;
            height: 0.729vw;
            background: var(--light);
          }
        }
      }

      tbody {
        display: grid;
        grid-template-columns: 3vw 13vw 11vw 9.5vw 8vw 8.5vw 4vw;
        color: var(--user-category);
        background: rgba(237, 237, 237, 1);
        font-size: 0.729vw;
        line-height: 0.885vw;
        font-weight: 500;
        padding: 0.5vw 0.5vw;
        align-items: center;

        .user-id {
          color: var(--user-id);
        }

        .user-title {
          color: var(--user-title);
        }

        .author {
          margin-left: 1vw;
        }

        .draft {
          display: flex;
          align-items: center;
          justify-content: center;
          margin-left: 0.417vw;
          pointer-events: none;
          opacity: 0.5;
          input {
            width: 0.729vw;
            height: 0.729vw;
            background: var(--light);
            border: 1px solid var(--btn-primary);
          }
        }

        &.styleChanged {
          background: var(--index2);
        }
      }
    }
  }

  .right {
    display: flex;
    flex-direction: column;

    font-size: 0.833vw;
    line-height: 0.99vw;
    font-weight: 400;

    width: 13.396vw;

    padding-bottom: 2vw;
    background: rgba(224, 224, 224, 1);

    .right-title {
      font-size: 1.042vw;
      line-height: 1.25vw;
      font-weight: 500;
      color: var(--light);
      background: var(--btn-primary);
      padding: 0.417vw 7.396vw 0.417vw 1.042vw;
    }

    .clear {
      display: flex;
      align-items: center;
      gap: 0.2vw;
      padding: 0.3vw 0.8vw;
      color: var(--font-date);
      cursor: pointer;
      border-bottom: 1px solid var(--divider);

      &:hover {
        color: var(--icon-color);
        transition: all 0.4s ease;
      }
    }

    form {
      .form-title {
        font-weight: 500;
        color: var(--font-date);
        margin: 0.781vw 0 0 1.042vw;
      }

      .calendar {
        display: flex;
        flex-direction: column;
        gap: 0.5vw;
        margin: 0.521vw 0 0.781vw 0.677vw;
        .date {
          display: flex;
          gap: 0.5vw;
          input {
            width: 5.208vw;
            font-size: 0.729vw;
            line-height: 0.885vw;
            outline: none;
          }
          i {
            color: var(--icon-color);
          }
        }
      }

      .btn {
        display: flex;
        margin-left: 1vw;
        margin-bottom: 1vw;

        button {
          cursor: pointer;
          height: 1.406vw;
          display: grid;
          width: 4.6vw;
          align-items: center;
          gap: 1vw;
          &:nth-child(1) {
            background: var(--btn-primary);
            color: var(--light);
          }

          &:nth-child(2) {
            color: var(--btn-primary);
            background: transparent;
            width: 6vw;
            margin-left: 0.5vw;

            &:hover {
              background: var(--btn-primary);
              transition: all 0.4s ease;
              color: var(--light);
            }
          }
        }
      }
    }

    .divider {
      width: 100%;
      height: 1px;
      background: var(--divider);
    }

    .user-list-container {
      border-bottom: 1px solid var(--divider);
      padding: 0.521vw 1.042vw;
      .user-list-title {
        display: flex;
        justify-content: space-between;
        color: var(--font-date);
        font-weight: 500;

        .icon {
          i {
            cursor: pointer;
          }
        }
      }

      .user-list {
        transition: all 0.4s ease;
        display: flex;
        flex-direction: column;
        button {
          color: var(--list-opacity-seven);
          margin-top: 0.521vw;
          cursor: pointer;
          transition: all 0.4s ease;
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          background: transparent;
          outline: none;
          color: var(--list-opacity-seven);

          &:hover,
          &:active,
          &:focus {
            color: var(--icon-color);
          }
        }
      }
    }

    .category-list-container {
      border-bottom: 1px solid var(--divider);
      padding: 0.521vw 1.042vw;
      .category-list-title {
        display: flex;
        justify-content: space-between;
        color: var(--font-date);
        font-weight: 500;

        .icon {
          i {
            cursor: pointer;
          }
        }
      }

      .category-list {
        display: flex;
        flex-direction: column;
        button {
          color: var(--list-opacity-seven);
          margin-top: 0.521vw;
          cursor: pointer;
          transition: all 0.4s ease;
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          background: transparent;
          outline: none;

          &:hover,
          &:active,
          &:focus {
            color: var(--icon-color);
          }
        }
      }
    }

    .drafts {
      display: flex;
      flex-direction: column;
      border-bottom: 1px solid var(--divider);
      padding: 0.521vw 1.042vw;

      .drafts-title {
        font-weight: 500;
        color: var(--user-category);

        margin-bottom: 0.313vw;
      }

      .drafts-btn {
        display: flex;
        justify-content: space-around;

        button {
          padding: 0.208vw 0.625vw 0.208vw 0.625vw;
          cursor: pointer;
          background: transparent;
          outline: none;
          color: var(--list-opacity-seven);
          &:focus,
          &:active,
          &:hover {
            background: var(--light);
            color: var(--btn-primary);
            transition: all 0.4s ease;
          }
        }
      }
    }

    .status-list {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      border-bottom: 1px solid var(--divider);
      color: var(--list-opacity-seven);
      margin-left: 0.417vw;
      padding: 0.521vw 0;

      h2 {
        font-size: 0.833vw;
        line-height: 0.99vw;
        font-weight: 500;
        color: var(--font-date);
        margin-left: 1.042vw;
      }
      button {
        cursor: pointer;
        margin-top: 0.208vw;
        background: transparent;
        outline: none;
        padding: 0.208vw 0.625vw;
        font-size: 0.833vw;
        line-height: 0.99vw;
        color: var(--list-opacity-seven);
        &:focus,
        &:active,
        &:hover {
          background: var(--light);
          color: var(--btn-primary);
          transition: all 0.4s ease;
        }
        &:nth-child(3),
        &:nth-child(4),
        &:nth-child(5) {
          margin-left: 0.5vw;
        }
      }
    }
  }
}

@media screen and (max-width: 1366px) {
  .admin_wrapper {
    .left {
      width: 100%;
      align-items: center;
      margin-top: 10vw;

      .title {
        font-size: 2.604vw;
        line-height: 2.604vw;
        margin-bottom: 1.953vw;
      }

      .left-header {
        display: grid;
        grid-template-areas:
          "search search"
          "pagination empty"
          "count_news filter";
        grid-template-columns: 30vw;
        align-items: center;

        .pagination {
          grid-area: pagination;
          margin: 2.604vw 0;
        }

        .empty {
          grid-area: empty;
          margin: 2.604vw 0;

          input {
            width: 4.557vw;
            height: 3.385vw;
          }

          h2 {
            font-size: 1.823vw;
            line-height: 2.214vw;
          }
        }

        .count_news {
          grid-area: count_news;

          margin-bottom: 1.302vw;
          font-size: 1.823vw;
          line-height: 2.214vw;
        }

        .search {
          grid-area: search;
          margin-bottom: 1.953vw;

          input {
            width: 30.599vw;
            height: 4.036vw;
          }

          button {
            width: 10.026vw;
            height: 4.036vw;
          }
        }

        .filter {
          grid-area: filter;
          display: flex;
          align-items: center;
          justify-content: center;
          text-align: left;
          width: 8.984vw;
          height: 3.906vw;
          font-size: 1.8vw;
          line-height: 2.2vw;
          color: var(--btn-primary);
          border: 1px solid var(--btn-primary);
          margin-bottom: 1.302vw;
          background: transparent;
        }
      }

      table {
        width: 95vw;
        overflow-y: scroll;

        &::-webkit-scrollbar {
          width: 0;
        }
        thead {
          display: grid;
          grid-template-columns: 6vw 49vw 23vw 25vw 22vw 20vw 10vw;
          font-size: 1.563vw;
          line-height: 1.953vw;
          width: max-content;
          align-items: center;
          height: 3.385vw;
        }

        tbody {
          display: grid;
          grid-template-columns: 6vw 49vw 25vw 24vw 24vw 17vw 10vw;
          font-size: 1.823vw;
          line-height: 2.214vw;
          width: max-content;
          margin-bottom: 1vw;

          .draft {
            input {
              width: 1.823vw;
              height: 1.823vw;
            }
          }
        }
      }

      .isIpad {
        display: flex;
        flex-direction: column;
        padding-bottom: 4vw;

        h2 {
          color: var(--font-black-opacity);
          font-weight: 400;
          font-size: 1.823vw;
          line-height: 2.214vw;
          margin-bottom: 4.167vw;
          margin-top: 2.865vw;

          span {
            font-weight: 500;
          }
        }
      }
    }
    .right {
      width: 100%;
      font-size: 2.083vw;
      line-height: 2.474vw;
      margin-top: 10vw;
      padding-bottom: 10vw;

      .right-title {
        font-size: 2.604vw;
        line-height: 3.125vw;
        display: flex;
        justify-content: space-between;
        width: 100%;
        padding: 1vw 0;

        h2 {
          margin-left: 2.604vw;
        }

        i {
          margin-right: 2.302vw;
        }
      }

      form {
        margin: 1vw 0;
        .form-title {
          margin-bottom: 1.302vw;
          margin-left: 2.604vw;
        }
        .calendar {
          margin-left: 4.604vw;
          margin-top: 1.302vw;
          .date {
            margin-top: 1.302vw;
          }
        }

        .btn {
          button {
            width: 13.151vw;
            height: 3.516vw;
            margin-top: 1.953vw;

            &:nth-child(2) {
              width: 17vw;
              margin-left: 3vw;
            }
          }
        }
      }

      .user-list-container {
        padding: 1.5vw;
      }

      .category-list-container {
        padding: 1.5vw;
      }

      .drafts {
        display: flex;
        justify-content: flex-start;
        align-items: flex-start;

        .drafts-title {
          margin-left: 2.604vw;
        }

        .drafts-btn {
          margin: 0.781vw 0 0.781vw 1.042vw;

          button {
            color: var(--user-category);
            margin: 0 2.297vw;
          }
        }
      }

      .status-list {
        padding: 1.302vw 0;
        margin-left: 1.042vw;

        h2 {
          font-size: 1.8vw;
          line-height: 2.2vw;
        }

        button {
          font-size: 1.8vw;
          line-height: 2.2vw;

          color: var(--user-category);

          &:nth-child(2) {
            margin-top: 0.781vw;
          }

          &:nth-child(3),
          &:nth-child(4),
          &:nth-child(5) {
            margin-left: 1.604vw;
            margin-top: 1.042vw;
          }
        }
      }
    }
  }
}

@media screen and (max-width: 414px) {
  .admin_wrapper {
    .left {
      margin-top: 15vw;
      .title {
        font-size: 5.333vw;
        line-height: 6.4vw;
      }
      .left-header {
        grid-template-columns: 60vw;
        gap: 1vw;
        margin-bottom: 2.6vw;

        .search {
          input {
            width: 62.667vw;
            height: 8.267vw;
          }
          button {
            width: 20.533vw;
            height: 8.267vw;
          }
        }

        .empty {
          input {
            width: 9.333vw;
            height: 6.933vw;
          }

          h2 {
            font-size: 3.733vw;
            line-height: 4.533vw;
            margin-left: 2.667vw;
          }
        }

        .count_news {
          font-size: 3.733vw;
          line-height: 4.533vw;
        }

        .filter {
          width: 18.4vw;
          height: 8vw;
          font-size: 3.7vw;
          line-height: 4.5vw;
        }
      }

      table {
        thead {
          grid-template-columns: 12vw 100vw 43vw 47vw 46vw 42vw 22vw;
          font-size: 3.2vw;
          line-height: 4vw;
          height: 6.933vw;
        }

        tbody {
          grid-template-columns: 10vw 95vw 43vw 43vw 43vw 40vw 20vw;
          font-size: 3.7vw;
          line-height: 4.5vw;
          padding-left: 2vw;
          gap: 3vw;
          padding-bottom: 2vw;

          .draft {
            input {
              width: 3.733vw;
              height: 3.733vw;
            }
          }
        }
      }

      .isIpad {
        h2 {
          font-size: 3.7vw;
          line-height: 4.5vw;
        }
      }
    }

    .right {
      font-size: 4.267vw;
      line-height: 5.067vw;
      margin-top: 18vw;
      padding-bottom: 22vw;
      margin-top: 15vw;

      .clear {
        padding: 2vw;
      }

      .right-title {
        font-size: 5.333vw;
        line-height: 6.4vw;
      }

      form {
        margin-top: 4vw;
        .form-title {
          margin-bottom: 2.667vw;
        }
        .calendar {
          margin-bottom: 4vw;
          .date {
            margin-left: 24vw;
          }
        }
        .btn {
          margin-left: 24vw;
          margin-bottom: 2.667vw;
          button {
            width: 28.933vw;
            height: 7.2vw;

            &:nth-child(2) {
              margin-left: 3vw;
              width: 32vw;
            }
          }
        }
      }

      .user-list-container {
        padding: 3vw;
      }

      .category-list-container {
        padding: 3vw;
      }

      .drafts {
        margin: 0;
        .drafts-title {
          margin-top: 2.667vw;
          margin-bottom: 1.6vw;
        }

        .drafts-btn {
          margin-bottom: 2.667vw;
          button {
            margin: 0;

            &:nth-child(2) {
              margin-left: 8.8vw;
            }
            &:nth-child(3) {
              margin-left: 8.8vw;
            }
          }
        }

        .status-list {
          button {
            &:nth-child(1) {
              margin-top: 1.6vw;
            }
            &:nth-child(2) {
              margin-top: 1.067vw;
            }
            &:nth-child(3),
            &:nth-child(4) {
              margin-top: 2.133vw;
            }
          }
        }
      }

      .status-list {
        padding: 3vw 0;
        gap: 1vw;
        margin-left: 3vw;
        h2 {
          font-size: 4.267vw;
          line-height: 5vw;
          margin-left: 2vw;
        }

        button {
          font-size: 4.267vw;
          line-height: 5vw;
        }
      }
    }

    .isIpad {
      margin-bottom: 4vw;
    }
  }
}
</style>
